// Rotation handle — positioned above the shape's north edge
// Drag to rotate the shape around its center

import * as THREE from 'three'
import type { Handle, EditableShape } from '@/types/editor'
import { DPM_SCALE } from '@/constants/coordinates'

export class RotationHandle implements Handle {
  readonly type = 'rotation' as const
  readonly cursor = 'grab'
  readonly mesh: THREE.Mesh

  private shape: EditableShape
  private startAngle: number = 0
  private startRotation: number = 0
  private centerWorld: { x: number; z: number } = { x: 0, z: 0 }

  constructor(mesh: THREE.Mesh, shape: EditableShape) {
    this.mesh = mesh
    this.shape = shape
  }

  onDragStart(worldPoint: THREE.Vector3): void {
    const center = this.shape.getCenter()
    this.centerWorld = {
      x: center.x * DPM_SCALE,
      z: center.y * DPM_SCALE,
    }
    this.startRotation = this.shape.getRotation()
    this.startAngle = Math.atan2(
      worldPoint.x - this.centerWorld.x,
      -(worldPoint.z - this.centerWorld.z)
    )
  }

  onDrag(worldPoint: THREE.Vector3): void {
    const currentAngle = Math.atan2(
      worldPoint.x - this.centerWorld.x,
      -(worldPoint.z - this.centerWorld.z)
    )
    const deltaRad = currentAngle - this.startAngle
    let newRotation = this.startRotation + deltaRad * (180 / Math.PI)

    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360

    // Snap to 1° increments
    newRotation = Math.round(newRotation)

    this.shape.rotate(newRotation)
  }

  onDragEnd(): void {
    this.shape.commit()
  }
}
